import { Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { ADatabaseConnectionsService, AUserPermissionsService, handleError, IDatabaseConnectionHolderWithFields } from "@coso-replicator/core";
import { ViewMode } from "@coso/forms";
import { suppressTabbingToNextCell, TemplateRendererComponent } from "@coso/grid";
import { LoadingService } from "@coso/loading";
import { DialogComponent, DialogType } from "@coso/utility";
import { GridOptions } from "ag-grid-community";
import { forkJoin, Observable } from "rxjs";
import { flatMap, map } from "rxjs/operators";
import { AddEditDatabaseConnectionComponent } from "./add-edit-database-connection/add-edit-database-connection.component";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";

export interface DatabaseConnection extends IDatabaseConnectionHolderWithFields {
  isCurrentlyInUse: boolean;
}

@Component({
  standalone:false,
  templateUrl: "./database-connections.component.html",
  styleUrls: ["./database-connections.component.scss"]
})
export class DatabaseConnectionsComponent implements OnInit {
  @ViewChild("actionButtons", { static: true }) actionButtons: TemplateRef<any>;
  modalRef: NgbModalRef;
  gridOptions: GridOptions;
  connections: DatabaseConnection[];
  errorMessage: string;
  successMessage: string;
  isReadOnly: boolean;

  constructor(
    public loadingService: LoadingService,
    private userPermissionsService: AUserPermissionsService,
    private dbConnectionsService: ADatabaseConnectionsService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.userPermissionsService.hasLimitedAccess$.subscribe((hasLimitedAccess) => (this.isReadOnly = hasLimitedAccess));
    this.gridOptions = {
      rowHeight: 40,
      suppressRowClickSelection: true,
      onFirstDataRendered: (e) => {
        e.api.setGridAriaProperty("label", "List of Database Connections");
      },
      defaultColDef: { sortable: true, resizable: true, minWidth: 100, suppressKeyboardEvent: suppressTabbingToNextCell },
      columnDefs: [
        { headerName: "Name", field: "databaseConnectionHolder.name" },
        {
          headerName: "Actions",
          sortable: false,
          resizable: false,
          maxWidth: 110,
          minWidth: 110,
          cellRenderer: TemplateRendererComponent,
          cellRendererParams: { ngTemplate: this.actionButtons }
        }
      ]
    };
    this.loadDatabaseConnections();
  }

  openDeleteModal(holder: IDatabaseConnectionHolderWithFields) {
    this.errorMessage = this.successMessage = undefined;
    const modalRef = this.modalService.open(DialogComponent, {
      keyboard: false,
      ariaLabelledBy: "modalTitle"
    });
    modalRef.componentInstance.options = {
      titleText: "Delete database connection",
      type: DialogType.Warning,
      contentText: `Are you sure you want to delete database connection "${holder.databaseConnectionHolder.name}"?`,
      rightButtons: [
        { label: "No", type: "button", class: "btn btn-dark mr-2", onClick: (modalRef) => modalRef.dismiss() },
        {
          label: "Yes",
          type: "button",
          class: "btn btn-primary",
          onClick: () => {
            this.loadingService.start(`Deleting database connection`);
            this.dbConnectionsService.delete(holder.databaseConnectionHolder.name).subscribe(
              () => {
                this.successMessage = "Database connection was successfully deleted.";
                this.loadDatabaseConnections();
                this.loadingService.stop();
                modalRef.dismiss();
              },
              (error) => {
                this.errorMessage = `Failed to delete database connection due to: ${handleError(error)}`;
                this.loadingService.stop();
              }
            );
          }
        }
      ],
      onClose: () => modalRef.dismiss()
    };
  }

  openAddEditModal(holder?: IDatabaseConnectionHolderWithFields) {
    this.modalRef = this.modalService.open(AddEditDatabaseConnectionComponent, {
      ariaLabelledBy: "modalTitle",
      backdrop: "static",
      size: "lg"
    });
    this.modalRef.componentInstance.viewMode = holder ? (this.isReadOnly ? ViewMode.View : ViewMode.Edit) : ViewMode.Create;
    this.modalRef.componentInstance.entity = holder;
    this.modalRef.componentInstance.saveCallback = holder ? this.update.bind(this, holder.databaseConnectionHolder.name) : this.create.bind(this);
  }

  private update(name: string, entity: IDatabaseConnectionHolderWithFields) {
    this.loadingService.start("Updating database connection");
    this.dbConnectionsService.update(name, entity).subscribe(
      () => {
        this.modalRef.dismiss();
        this.successMessage = `Database connection '${entity.databaseConnectionHolder.name}' has been successfully updated.`;
        this.loadDatabaseConnections();
        this.loadingService.stop();
      },
      (error) => {
        this.modalRef.componentInstance.errorMessage = `Failed to update database connection due to: ${handleError(error)}`;
        this.loadingService.stop();
      }
    );
  }

  private create(entity: IDatabaseConnectionHolderWithFields) {
    this.loadingService.start("Creating database connection");
    this.dbConnectionsService.create(entity).subscribe(
      () => {
        this.modalRef?.dismiss();
        this.successMessage = `Database connection '${entity.databaseConnectionHolder.name}' has been successfully created.`;
        this.loadDatabaseConnections();
        this.loadingService.stop();
      },
      (error) => {
        this.modalRef.componentInstance.errorMessage = `Failed to create database connection due to: ${handleError(error)}`;
        this.loadingService.stop();
      }
    );
  }

  private loadDatabaseConnections() {
    this.loadingService.start("Loading database connections", "loadDatabaseConnections");
    this.dbConnectionsService
      .get()
      .pipe(
        flatMap((connections) => {
          const observables$: Observable<DatabaseConnection>[] = [];
          for (const connection of connections) {
            const isCurrentlyInUse$ = this.dbConnectionsService
              .isInUse(connection.databaseConnectionHolder.name)
              .pipe(map((isCurrentlyInUse) => ({ ...connection, isCurrentlyInUse: isCurrentlyInUse })));
            observables$.push(isCurrentlyInUse$);
          }
          return forkJoin(observables$);
        })
      )
      .subscribe(
        (data) => {
          this.connections = data;
          this.loadingService.stop("loadDatabaseConnections");
        },
        (error) => {
          this.errorMessage = `Failed to load the database connections due to: ${handleError(error)}`;
          this.loadingService.stop("loadDatabaseConnections");
        }
      );
  }
}
